import { Modal } from '@components/core/modal.component';
import { Button } from '@components/ui/button';
import { useAppForm } from '@components/ui/form/form';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useAdminAvailableParentCategoriesQuery, useAdminCreateCategory } from '@queries/admin-categories.query';
import { categorySchema } from '@schemas/category.schema';
import { createFormInvalidHandler } from '@utils/form.util';
import { mapToSelectOptions } from '@utils/select.util';
import { Loader2Icon } from 'lucide-react';
import { parseAsString, useQueryState } from 'nuqs';
import React from 'react';
import { adminCategoryFormOptions } from './admin-category-form-options';

type AdminCategoryCreateModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const AdminCategoryCreateModal: React.FC<AdminCategoryCreateModalProps> = (props) => {
  const { isOpen, onClose } = props;
  const [, setSelectedCategoryId] = useQueryState('categoryId', parseAsString);
  const { mutateAsync: createCategory, isPending } = useAdminCreateCategory();
  const { data: parentCategoriesData } = useAdminAvailableParentCategoriesQuery();

  const form = useAppForm({
    ...adminCategoryFormOptions,

    validators: {
      onSubmit: categorySchema,
    },

    onSubmit: async ({ value }) => {
      await createCategory(
        {
          description: value.description || undefined,
          name: value.name ?? '',
          parentCategoryId: value.parentCategoryId ?? undefined,
        },
        {
          onSuccess: (data) => {
            successToast({ description: 'Category created successfully' });
            void setSelectedCategoryId(data.id);
            handleClose();
          },
          onError: (error) => errorToast({ description: error.message || 'Failed to create category' }),
        },
      );
    },
    onSubmitInvalid: createFormInvalidHandler(),
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const parentOptions = mapToSelectOptions(parentCategoriesData?.categories ?? []);

  if (!isOpen) return null;

  return (
    <Modal
      centered
      isOpen={isOpen}
      onClose={handleClose}
      header={{
        className: 'text-left',
        title: 'Create Category',
      }}
      footer={{
        rightButton: (
          <div className="flex gap-2 justify-end">
            <Button
              disabled={isPending}
              onClick={handleClose}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              disabled={isPending}
              onClick={() => form.handleSubmit()}
              type="button"
            >
              {isPending && <Loader2Icon className="size-3 mr-1 animate-spin" />}
              Save
            </Button>
          </div>
        ),
      }}
      width="640px"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <form.AppForm>
          <div className="flex flex-col gap-4">
            <form.AppField name="parentCategoryId">
              {(field) => (
                <field.SelectPopoverField
                  label="Parent Category"
                  onClear={() => field.handleChange(undefined)}
                  options={parentOptions}
                  placeholder="Select a parent category"
                />
              )}
            </form.AppField>

            <form.AppField name="name">
              {(field) => (
                <field.TextField
                  autoComplete="off"
                  data-1p-ignore
                  label="Name"
                  placeholder="Enter a category name"
                />
              )}
            </form.AppField>

            <form.AppField name="description">
              {(field) => (
                <field.TextAreaField
                  label="Description"
                  placeholder="Enter a category description"
                  rows={3}
                />
              )}
            </form.AppField>
          </div>
        </form.AppForm>
      </form>
    </Modal>
  );
};
